import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  Cloud,
  Code,
  Database,
  FileText,
  Globe,
  HardDrives,
  Plug,
  Stack,
  WebhooksLogo
} from '@phosphor-icons/react';
const Integrations = () => {
  const integrations = [
    { name: 'OpenAI', category: 'AI/LLM', logo: '🤖' },
    { name: 'Anthropic', category: 'AI/LLM', logo: '🧠' },
    { name: 'Google AI', category: 'AI/LLM', logo: '🔮' },
    { name: 'Azure OpenAI', category: 'AI/LLM', logo: '☁️' },
    { name: 'Mistral AI', category: 'AI/LLM', logo: '🌀' },
    { name: 'Twilio', category: 'Voice/SMS', logo: '📞' },
    { name: 'Telnyx', category: 'Voice/SMS', logo: '📱' },
    { name: 'Microsoft', category: 'Productivity', logo: '🪟' },
    { name: 'Google Calendar', category: 'Productivity', logo: '📅' },
    { name: 'Xero', category: 'Accounting', logo: '📊' }
  ];

  // MCP data sources
  const mcpSources = [
    { icon: Database, name: 'Databases', examples: 'PostgreSQL, MySQL, MongoDB' },
    { icon: Cloud, name: 'Cloud Storage', examples: 'AWS S3, Google Cloud, Azure' },
    { icon: FileText, name: 'Documents', examples: 'PDFs, Word, Excel, Google Docs' },
    { icon: Globe, name: 'APIs', examples: 'REST, NetworkQL, Webhooks' },
    { icon: HardDrives, name: 'Enterprise', examples: 'Salesforce, SAP, ServiceNow' },
    { icon: Code, name: 'Dev Tools', examples: 'GitHub, GitLab, Jira' },
  ];

  return (
    <>
      <SEO title="Integrations - Man & Machine" description="Connect Man & Machine with your favorite tools and services." />
      <PublicNavHeader />
      
      <div className="min-h-screen bg-[#0a0a0f]">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/10 via-transparent to-[#EC4899]/10" />
          
          <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] text-sm font-medium mb-6">
                <Plug size={16} />
                Integrations
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Connect Your Tools
              </h1>
              
              <p className="text-lg text-[#A0AEC0]">
                Integrate Man & Machine with the tools you already use.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {integrations.map((integration, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="glass rounded-xl p-5 text-center hover:border-[#6366F1]/30 transition-all cursor-pointer group"
              >
                <div className="text-4xl mb-3">{integration.logo}</div>
                <h3 className="font-semibold text-white mb-1">{integration.name}</h3>
                <span className="text-xs text-[#718096]">{integration.category}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* MCP Section */}
        <section className="max-w-7xl mx-auto px-6 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-8 border border-[#14F195]/20"
          >
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#14F195] to-[#06B6D4] flex items-center justify-center">
                  <Stack size={40} className="text-white" />
                </div>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-2xl font-bold text-white">Model Context Protocol (MCP)</h2>
                  <span className="px-2 py-0.5 text-xs font-medium bg-[#14F195]/20 text-[#14F195] rounded-full">Universal</span>
                </div>
                <p className="text-[#A0AEC0] mb-6 max-w-2xl">
                  Connect your AI agents to any data source through the open Model Context Protocol. 
                  No custom integration code needed — one standard protocol for universal connectivity.
                </p>
                
                {/* MCP Data Sources Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                  {mcpSources.map((source, i) => (
                    <div key={i} className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3 text-center">
                      <source.icon size={24} className="text-[#14F195] mx-auto mb-2" />
                      <p className="text-sm font-medium text-white">{source.name}</p>
                      <p className="text-xs text-[#718096] mt-1">{source.examples}</p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/mcp"
                  className="inline-flex items-center gap-2 text-[#14F195] hover:text-[#06B6D4] transition-colors font-medium"
                >
                  Learn more about MCP
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Webhooks Section */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-2xl p-8"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
                  <WebhooksLogo size={40} className="text-white" />
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-white mb-2">Webhooks</h2>
                <p className="text-[#A0AEC0] mb-4">
                  Connect to thousands of apps through webhooks. Works with any automation platform.
                </p>
                
                {/* Automation Platform Logos */}
                <div className="flex items-center gap-6 justify-center md:justify-start">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-[#FF4A00]/10 flex items-center justify-center">
                      <span className="text-xl">⚡</span>
                    </div>
                    <span className="text-xs text-[#718096]">Zapier</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                      <span className="text-xl">🔗</span>
                    </div>
                    <span className="text-xs text-[#718096]">Make.com</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-[#14F195]/10 flex items-center justify-center">
                      <span className="text-xl">🔄</span>
                    </div>
                    <span className="text-xs text-[#718096]">n8n</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="text-center">
            <p className="text-[#A0AEC0] mb-4">Need a custom integration?</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
            >
              Contact Us
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default Integrations;